import { useCallback, useMemo, useState } from 'react'
import styles from './GameReport.module.css'
import {
  PAGE_SIZE_OPTIONS,
  PlayerSortOption,
  ReportSortDir,
  useReportData,
} from '../../../../contexts/GameReportContextProvider'
import { CellElement, NO_POINTS_SKIP, Table, TableRow } from '../../../reports/components/GenericTable'
import { BadgeImage } from '../Badges/BadgeView/BadgeImage'
import { Tooltip } from '../../../../common/components/tooltip/Tooltip'
import Chart from 'react-google-charts'
import { formatTime } from '../../../../util/number'
import { t } from 'i18next'
import { SingleValue } from 'react-select'
import { PaginationSelectOption } from '../../../../composites/GamesOverview/components/PaginationControls'
import { getIcon } from '../../../../common/components/icons/utils'
import { backendTimeToLocalTime } from '@/util/date'

type PlayersTabProps = {
  maxPoints?: number
  customCompletionEnabled?: boolean
  noPointsGame?: boolean
}

export const PlayersTab: React.FC<PlayersTabProps> = ({ maxPoints, customCompletionEnabled, noPointsGame }) => {
  const [activePlayerSortCol, setActivePlayerSortCol] = useState<PlayerSortOption>()
  const {
    currentPage,
    pageSize,
    allPageOptions,
    setPage,
    lastPage,
    next,
    prev,
    selectPageSize,
    loading,
    setPlayerSort,
    playerCount,
    pageData,
  } = useReportData()

  const handlePlayerSort = useCallback(
    (sortKey: string) => (direction: PlayerSortOption | string) => {
      if (setPlayerSort) {
        setPlayerSort(sortKey as PlayerSortOption, direction as ReportSortDir)
        setActivePlayerSortCol(sortKey as PlayerSortOption)
      }
    },
    [setPlayerSort],
  )

  const onChangePageSize = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      selectPageSize(option!.value)
    },
    [selectPageSize],
  )

  const onChangeCurrentPage = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      setPage(option!.value)
    },
    [setPage],
  )
  const playersTableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []

    const pieOptions = {
      legend: 'none',
      pieSliceText: 'none',
      tooltip: { trigger: 'none' },
      chartArea: { width: '95%', height: '100%' },
      slices: {
        0: { color: '#018831' },
        1: { color: '#E8A3C4', offset: 0.1 }, //pink-50
      },
    }

    pageData?.forEach((row) => {
      const tableRow: CellElement[] = [
        row.rank,
        <>
          <div>{row.nickName}</div>
          {row.email && <div className={styles.playerSubDetailRow}>{row.email}</div>}
          {row.teamMemberNames && <div className={styles.playerSubDetailRow}>{row.teamMemberNames}</div>}
          {row.awardedBadges.length > 0 && (
            <div className={styles.playerSubDetailsBadges}>
              {row.awardedBadges.map((badge) => {
                return (
                  <Tooltip key={badge.id} timeoutMs={50} tooltipContent={badge.name + ''}>
                    {(tooltipProps) => (
                      <div {...tooltipProps}>
                        <BadgeImage url={badge.imageUrl} variant='supertiny' />
                      </div>
                    )}
                  </Tooltip>
                )
              })}
            </div>
          )}
        </>,
        noPointsGame ? NO_POINTS_SKIP : row.points,
        <div className={styles.correctCell}>
          <Chart
            chartType='PieChart'
            width='26px'
            height='26px'
            options={pieOptions}
            data={[
              ['isCorrect', 'value'],
              ['Correct', Math.min(100, row.correctPercentage)],
              ['Not correct', 100 - Math.min(100, row.correctPercentage)],
            ]}
          />
          {row.correctPercentage.toFixed() + '%'}
        </div>,
        backendTimeToLocalTime(row.createdAt),
        backendTimeToLocalTime(row.completedAt),
        formatTime(row.gameDuration),
        <div className={styles.completionCell}>
          {row.overallCompleted && customCompletionEnabled ? (
            <Tooltip
              timeoutMs={50}
              tooltipContent={t(
                'game_summary.players_table.completed_cell_tooltip',
                'Completed based on completion rules',
              )}
            >
              {(tooltipProps) => (
                <span {...tooltipProps}>
                  {row.completionRate.toFixed() + '%'}
                  {getIcon('circleCheckmark')}
                </span>
              )}
            </Tooltip>
          ) : (
            <span>{row.completionRate.toFixed() + '%'}</span>
          )}
        </div>,
      ]
      rows.push(tableRow)
    })
    return rows
  }, [customCompletionEnabled, noPointsGame, pageData])

  return (
    <Table
      isSortable
      hasPagination
      headerWidths={['13%', '280px'].concat(noPointsGame ? ['17%', '17%', '17%'] : ['13%', '13%', '13%', '13%'])}
      headerLabels={[
        t('game_summary.players_table.rank', 'Rank'),
        t('game_summary.players_table.name', 'Name'),
        noPointsGame ? NO_POINTS_SKIP : t('game_summary.players_table.points', 'Points'),
        t('game_summary.players_table.correct', 'Correct'),
        t('game_summary.players_table.start_time', 'Started'),
        t('game_summary.players_table.completed_time', 'Completed'),
        t('game_summary.players_table.spent_time', 'Spent time'),
        t('game_summary.players_table.complete_percentage', 'Completion'),
      ]}
      headerSortTags={[
        'rank',
        'nickName',
        noPointsGame ? NO_POINTS_SKIP : 'points',
        'correctPercentage',
        'createdAt',
        'completedAt',
        'gameDuration',
        'completionRate',
      ]}
      onSort={handlePlayerSort}
      activeSortCol={activePlayerSortCol}
      rows={playersTableRowData}
      currentPage={currentPage}
      pageSize={pageSize}
      lastPage={lastPage}
      allPageOptions={allPageOptions}
      onClickNext={next}
      onClickPrev={prev}
      onChangePageSize={onChangePageSize}
      onChangeCurrentPage={onChangeCurrentPage}
      loading={loading}
      pageSizeOptions={PAGE_SIZE_OPTIONS.concat({
        value: playerCount,
        label: t('game_summary.show_all_players', 'All'),
      })}
      itemsPerPageLabel={t('game_summary.pagination.players_per_page', 'players per page')}
      tableTitle={t('game_summary.players_table.table_title', 'Players')}
      subTitleRow1={t('game_summary.players_table.sub_title1', '%{amount} players', { amount: playerCount })}
      subTitleRow2={
        noPointsGame
          ? undefined
          : t('game_summary.players_table.sub_title2', 'Total points: %{points}', {
              points: maxPoints,
            })
      }
    />
  )
}
